<script lang="ts">
    import { beforeUpdate, onDestroy, onMount } from "svelte";
    import { ApiClient } from "../../../../GlobalHelper/ApiClient";
    import { networkStore } from "../../../../store/networks";
    import { user } from "../../../../store/stores";
    import PlaysCounter from "./Components/PlaysCounter.svelte";
    import SvelteTable from "svelte-table";
    import IdColumn from "./Components/DashboardTable/IdColumn.svelte";
    import PreviewColumn from "./Components/DashboardTable/PreviewColumn.svelte";
    import { PagedResponseOfNetworkListEntryFromJSON } from "../../../../../admin-service-typescript-client";
    import PlaysTimeline from "./Components/PlaysTimeline.svelte";
    import BaseRowElement from "./Components/DashboardTable/BaseRowElement.svelte";
    import dayjs, { Dayjs } from "dayjs";
    import type {
        IAggregationDataResponse,
        IAggregationResponseItem,
        ICreativeStats,
        IPopObject,
        ITimeRangeStats,
        rangeObject,
    } from "../../../../Types/Stats";
    import { statsStore } from "../../../../store/stats";
    import EventBus from "../../../../GlobalHelper/VertxBridge";
    import Admin from "../../_Admin/Admin.svelte";
    import weekday from "dayjs/plugin/weekday";
    import de from "dayjs/locale/de";
    import { eventbusUrl } from "../../../../settings/settings";
    import TimeSelectDropdown from "./Components/TimeSelectDropdown.svelte";
    import CheckboxElement from "./Components/DashboardTable/CheckboxElement.svelte";
    import { uistate } from "../../../../store/uisstate";

    dayjs.extend(weekday).locale(de);
    let graphLabels: string[] = [];
    let iconAsc = "↑";
    let iconDesc = "↓";
    let sortBy = "id";
    let sortOrder = 1;
    let rows = [];
    let loading = true;
    let searchObject: { [key: string]: any } = { creativeId: "" };

    let range = {
        text: "Heute",
        start: dayjs()
            .set("ms", 0)
            .set("s", 0)
            .set("minute", 0)
            .set("hour", 0)
            .weekday(0),
        // .set("hour", 0),
        end: dayjs().add(1, "h"),
    };

    // define data
    $: rows = $statsStore.creativeStats.map((creativeStat) => {
        return {
            creativeId: creativeStat.creativeId,
            playouts: creativeStat.playouts,
            impressions: creativeStat.impressions,
            turnover: creativeStat.turnover,
            timeRangeBuckets: creativeStat.timeRangeBuckets,
        };
    });

    let checkedRows = [];

    // define column configs
    const columns = [
        {
            key: "checkbox",
            title: "",
            value: CheckboxElement,
            renderComponent: {
                component: CheckboxElement,
                props: {
                    idKey: "creativeId",
                    storeKey: "selectedCampaigns",
                },
            },
        },
        {
            key: "creativeId",
            title: "ID",
            value: (v: ICreativeStats) => v?.creativeId,
            sortable: true,
            renderComponent: BaseRowElement,
        },
        {
            key: "playouts",
            title: "Pops",
            value: (v: ICreativeStats) => v?.playouts,
            sortable: true,
            renderComponent: BaseRowElement,
        },
        {
            key: "impressions",
            title: "Impressions",
            value: (v: ICreativeStats) => v?.impressions,
            sortable: true,
            renderComponent: BaseRowElement,
        },
        {
            key: "turnover",
            title: "Vor. Einnahmen",
            value: (v: ICreativeStats) => v?.turnover,
            sortable: true,
            renderComponent: BaseRowElement,
        },
        // {
        //     key: "pet",
        //     title: "Vorschau",
        //     renderComponent: PreviewColumn,
        // },
    ];

    const initTimeRangeBuckets = (start: Dayjs, byDay: boolean = false) => {
        const array: ITimeRangeStats[] = [];
        const currentHour = dayjs()
            .set("minute", 0)
            .set("second", 0)
            .set("millisecond", 0);

        if (byDay) {
            for (let i = currentHour.diff(start, "days", false); i > 0; i--) {
                array.push({
                    start: currentHour.subtract(i, "days").valueOf(),
                    end:
                        i > 1
                            ? currentHour.subtract(i - 1, "days").valueOf()
                            : null,
                    playouts: 0,
                    impressions: 0,
                    turnover: 0,
                });
            }
            return array;
        }

        for (let i = currentHour.diff(start, "hours", false); i > 0; i--) {
            array.push({
                start: currentHour.subtract(i, "hours").valueOf(),
                end:
                    i > 1
                        ? currentHour.subtract(i - 1, "hours").valueOf()
                        : null,
                playouts: 0,
                impressions: 0,
                turnover: 0,
            });
        }
        return array;
    };

    const parsePopsByVideoResponse = (
        data: IAggregationDataResponse,
        initialBuckets: ITimeRangeStats[],
        byDay: boolean = false
    ) => {
        createLabels();
        const sortedData: { [key: string]: ITimeRangeStats[] } = {};
        data.body.results.forEach((result: IAggregationResponseItem) => {
            if (!sortedData[result.id.videoId]) {
                sortedData[result.id.videoId] = [...initialBuckets];
            }
            const slotArray: ITimeRangeStats[] = sortedData[result.id.videoId];
            const slotIndex = slotArray.findIndex(
                (slot) => slot.start === dayjs(result.id.timestamp).valueOf()
            );

            if (slotIndex !== -1) {
                slotArray[slotIndex] = {
                    ...slotArray[slotIndex],
                    end: dayjs(result.id.timestamp)
                        .add(1, byDay ? "day" : "hour")
                        .valueOf(),
                    playouts: result.count,
                    impressions: result.impressions,
                    turnover:
                        result.id.videoId !== "EXoQH3prqQjv6SmzSuCEox"
                            ? (result.impressions / 1000) * 7.5
                            : 0,
                };
            }
        });

        const newCreateStats: ICreativeStats[] = [];
        Object.keys(sortedData).forEach((key) =>
            newCreateStats.push({
                creativeId: key,
                timeRangeBuckets: sortedData[key],
                playouts: sortedData[key].reduce((playouts, data) => {
                    return playouts + data.playouts;
                }, 0),
                impressions: sortedData[key].reduce((impressions, data) => {
                    return impressions + data.impressions;
                }, 0),
                turnover: sortedData[key].reduce((turnover, data) => {
                    return turnover + data.turnover;
                }, 0),
            })
        );

        statsStore.update((statsStore) => {
            return {
                ...statsStore,
                creativeStats: newCreateStats,
            };
        });
        // const filledBuckets =
    };

    let timeoutRepo = {};

    const addMissingBuckets = (newArray, creativeId) => {
        const now = dayjs();
        timeoutRepo[creativeId] = setTimeout(function () {
            createLabels();
            newArray[59].end = now.valueOf();
            newArray.shift();
            newArray.push({
                start: now.valueOf(),
                end: null,
                playouts: 0,
                impressions: 0,
                turnover: 0,
            });
            statsStore.update((statsStore) => {
                return {
                    ...statsStore,
                    creativeStats: statsStore.creativeStats.map(
                        (creativeStat) => {
                            if (creativeStat.creativeId !== creativeId)
                                return creativeStat;
                            return {
                                ...creativeStat,
                                timeRangeBuckets: newArray,
                            };
                        }
                    ),
                };
            });
            addMissingBuckets(newArray, creativeId);
        }, now.diff(
            dayjs().add(1, "minutes").set("second", 0).set("millisecond", 0),
            "milliseconds"
        ) * -1);
    };

    const createLabels = (): void => {
        const array: string[] = [];
        221211;
        const now = dayjs();
        for (let i = 60; i > 0; i--) {
            array.push(now.subtract(i - 1, "minutes").format("HH:mm"));
        }
        graphLabels = array;
    };

    const userInteraction = async () => {
        await $user.updateToken().then(async () => {});
    };

    const initEventBus = () => {
        statsStore.update((statsStore) => {
            return {
                ...statsStore,
                eventBus: new EventBus(eventbusUrl),
            };
        });
    };

    const changeDateRange = (newRange: rangeObject) => {
        loading = true;
        range = newRange;
        $statsStore.eventBus.send(
            "channel://aggregates/groupByOrgIdAndVideoId",
            {
                orgId: "4cd3f13b-604f-4ba8-baea-3c165d6b5bd3",
                from: range.start.unix(),
                to: range.end.unix(),
            },
            function (error, message) {
                loading = false;
                if (error) return;
                const initialBuckets = initTimeRangeBuckets(range.start, false);
                parsePopsByVideoResponse(message, initialBuckets, false);
                // fillBucketsWithDataResponse(message);
                createLabels();
            }
        );
    };

    onDestroy(() => {
        Object.keys(timeoutRepo).forEach((key) =>
            clearTimeout(timeoutRepo[key])
        );
    });
    onMount(() => {
        userInteraction();
        initEventBus();

        $statsStore.eventBus.initializeConnection().then(() => {
            loading = true;
            const start = range.start;
            const end = range.end;
            const initialBuckets = initTimeRangeBuckets(start);
            $statsStore.eventBus.send(
                "channel://aggregates/groupByOrgIdAndVideoId",
                {
                    orgId: "4cd3f13b-604f-4ba8-baea-3c165d6b5bd3",
                    from: start.unix(),
                    to: end.unix(),
                },
                function (error, message) {
                    loading = false;
                    parsePopsByVideoResponse(message, initialBuckets);
                }
            );
        });
    });
</script>

<div class="dashboard-wrapper">
    <TimeSelectDropdown
        changeDateCallback={changeDateRange}
        changeSearchCallback={(e) => {
            searchObject = { creativeId: e.target.value };
            checkedRows = [];
        }}
        exportActivated={true}
        exportFileName={"Creatives_"}
        exportObject={{
            header: {
                creativeId: "Creative ID",
                playouts: "Playouts",
                impressions: "Impressionen",
                turnover: "Umsatz",
            },
            data: $uistate.selectedCampaigns,
        }}
    />
    {#if loading}
        <div class="box">
            <span class="icon-text has-text-info" style="margin: auto;">
                <span class="icon is-large">
                    <i
                        class="fas fa-spinner fa-pulse"
                        style="height: 1.5rem; width: 1.5rem;"
                    />
                </span>
                <span
                    style="margin: auto; padding-left: 10px; font-size: 1.25rem;"
                    >Loading ...</span
                >
            </span>
        </div>
    {:else}
        <div class="table-wrapper">
            <!-- <Table cols={columns} rows /> -->
            <SvelteTable
                {columns}
                rows={rows.filter((row) => {
                    return searchObject?.creativeId?.length > 0
                        ? row?.creativeId &&
                              row?.creativeId
                                  ?.toLowerCase()
                                  .search(
                                      searchObject?.creativeId?.toLowerCase()
                                  ) !== -1
                        : true;
                })}
                showExpandIcon={true}
                expandSingle={false}
                bind:sortBy
                bind:sortOrder
                expandRowKey="creativeId"
                classNameTable="space-between-rows"
                classNameRow="row-as-card"
                classNameCell="td-vertical-centered"
                classNameCellExpand="td-vertical-centered-expanded"
                classNameThead="thead-text-centered"
                style="border-collapse: separate;"
            >
                <svelte:fragment
                    slot="header"
                    let:sortOrder={refSortOrder}
                    let:sortBy={refSortBy}
                    ><tr>
                        {#each columns as col}
                            {#if col.key === "checkbox"}
                                <th>
                                    <input
                                        type="checkbox"
                                        on:change={() => {
                                            uistate.update((uistate) => {
                                                return {
                                                    ...uistate,
                                                    selectedCampaigns:
                                                        uistate
                                                            .selectedCampaigns
                                                            .length ===
                                                        rows.length
                                                            ? []
                                                            : [...rows],
                                                };
                                            });
                                        }}
                                        checked={$uistate.selectedCampaigns
                                            .length === rows.length}
                                    />
                                </th>
                            {:else}
                                <th
                                    on:click={() => {
                                        sortBy = col.key;
                                        sortOrder = sortOrder > 0 ? -1 : 1;
                                    }}
                                >
                                    {refSortBy === col.key
                                        ? refSortOrder > 0
                                            ? iconAsc
                                            : iconDesc
                                        : ""}
                                    {col.title}
                                </th>
                            {/if}
                        {/each}
                        <th />
                    </tr>
                </svelte:fragment>
                <svelte:fragment slot="expanded" let:row>
                    <div class="columns is-multiline" style="padding: 20px;">
                        <div class="column is-half-desktop is-full-tablet">
                            <PlaysTimeline
                                id={row.creativeId + "impressions"}
                                {range}
                                title=""
                                labels={row.timeRangeBuckets.map((bucket) =>
                                    dayjs(bucket.start).format("DD.MM HH:mm")
                                )}
                                dataset={[
                                    {
                                        label: "Impressions",
                                        data: row.timeRangeBuckets.map(
                                            (timeRangeBucket) =>
                                                timeRangeBucket.impressions
                                        ),
                                        backgroundColor: [
                                            "rgba(54, 162, 235, 0.2)",
                                        ],
                                        borderColor: ["rgba(54, 162, 235, 1)"],
                                        borderWidth: 1,
                                    },
                                ]}
                            />
                        </div>
                        <div class="column is-half-desktop is-full-tablet">
                            <PlaysTimeline
                                id={row.creativeId + "turnover"}
                                {range}
                                title=""
                                labels={row.timeRangeBuckets.map((bucket) =>
                                    dayjs(bucket.start).format("DD.MM HH:mm")
                                )}
                                dataset={[
                                    {
                                        label: "Einnahmen",
                                        data: row.timeRangeBuckets.map(
                                            (timeRangeBucket) =>
                                                timeRangeBucket.turnover
                                        ),
                                        backgroundColor: [
                                            "rgba(54, 162, 235, 0.2)",
                                        ],
                                        borderColor: ["rgba(54, 162, 235, 1)"],
                                        borderWidth: 1,
                                    },
                                ]}
                            />
                        </div>
                    </div>
                </svelte:fragment>
            </SvelteTable>
        </div>
    {/if}
</div>

<style>
    .table-wrapper {
        overflow: auto;
    }

    tr th:hover {
        cursor: pointer;
    }

    .dashboard-wrapper {
    }
</style>
